import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { ApiService } from '../api/api.service';
import { extractData } from '../api/api-response.util';
import { officeInitials } from './office-initials.util';

/** Marca pública do escritório: o que aparece no menu, no login, no portal e na aba. */
export interface OfficeBranding {
  display_name: string | null;
  initials: string | null;
  logo_url: string | null;
}

const PRODUCT_NAME = 'Lexium';
const DEFAULT_FAVICON = '/favicon.ico';

/**
 * Carrega uma vez a marca do escritório (rota pública, funciona antes do login) e mantém o
 * ícone da aba em sincronia: logo do escritório, senão as iniciais, senão o ícone padrão.
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly api = inject(ApiService);
  private readonly document = inject(DOCUMENT);

  private readonly state = signal<OfficeBranding | null>(null);
  private requested = false;

  /** Nome do escritório; sem perfil cadastrado, o nome do sistema. */
  readonly displayName = computed(() => this.state()?.display_name?.trim() || PRODUCT_NAME);
  readonly hasOffice = computed(() => Boolean(this.state()?.display_name?.trim()));
  readonly initials = computed(() => this.state()?.initials?.trim() || officeInitials(this.state()?.display_name));
  readonly logoUrl = computed(() => this.state()?.logo_url || null);

  constructor() {
    effect(() => this.applyFavicon(this.logoUrl(), this.initials()));
  }

  /** Busca de novo (ex.: depois de salvar o nome do escritório). */
  refresh(): void {
    this.requested = false;
    this.load();
  }

  load(): void {
    if (this.requested) return;
    this.requested = true;

    this.api.get<unknown>('branding').subscribe({
      next: (response) => this.set(extractData(response) as Partial<OfficeBranding>),
      error: () => this.requested = false,
    });
  }

  /** Atualiza a marca na hora (ex.: depois de enviar ou remover a logo). */
  set(branding: Partial<OfficeBranding> | null | undefined): void {
    this.state.set({
      display_name: branding?.display_name ?? null,
      initials: branding?.initials ?? null,
      logo_url: branding?.logo_url ?? null,
    });
  }

  private applyFavicon(logoUrl: string | null, initials: string): void {
    const head = this.document?.head;
    if (!head) return;

    let link = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      head.appendChild(link);
    }

    if (logoUrl) {
      link.removeAttribute('type');
      link.href = logoUrl;
    } else if (initials) {
      link.type = 'image/svg+xml';
      link.href = initialsIcon(initials);
    } else {
      link.type = 'image/x-icon';
      link.href = DEFAULT_FAVICON;
    }
  }
}

/** Ícone da aba com as iniciais: quadrado azul-marinho e letras em latão. */
export function initialsIcon(initials: string): string {
  const text = initials.slice(0, 2).toUpperCase().replace(/[^\p{L}\p{N}]/gu, '');
  const fontSize = text.length > 1 ? 30 : 38;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="12" fill="#13233F"/>` +
    `<text x="32" y="33" text-anchor="middle" dominant-baseline="central" fill="#D3A35C" ` +
    `font-family="Georgia, 'Times New Roman', serif" font-size="${fontSize}" font-weight="600">${text}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
